import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../../loading/spinner";
import DentalPrintInvoice from "./dentalPrintInvoice";

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const saveAppointments = (appointments) => {
  localStorage.setItem("appointments", JSON.stringify(appointments));
};

const getDentalServices = (appointmentId) => {
  try {
    const key = `dentalServices_${appointmentId}`;
    return JSON.parse(localStorage.getItem(key)) || null;
  } catch {
    return null;
  }
};

const saveDentalServices = (appointmentId, services) => {
  const key = `dentalServices_${appointmentId}`;
  localStorage.setItem(key, JSON.stringify(services));
};

// Default dental services list (if none stored)
const DEFAULT_SERVICES = [
  "General Dentistry",
  "Cosmetic Dentistry",
  "Dental Check Up",
  "Restoration And Filling",
  "Crowns And Bridges",
  "Fracture And Trauma",
  "Vaneers And Laminates",
  "Extraction",
  "Oral And Maxillofacial Surgery",
  "Orthodontics Treatment",
  "Child Dentistry",
  "Implant",
  "Scaling And Teeth Cleaning",
  "Root Canal Treatment",
];

// ============================================
// MAIN COMPONENT
// ============================================

const UnpaidtoPaid = ({ patientDetails, reset }) => {
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [showInvoice, setShowInvoice] = useState(false);

  // Load services from localStorage on mount
  useEffect(() => {
    if (!patientDetails?.id) return;

    // Try to get existing services
    let savedServices = getDentalServices(patientDetails.id);
    if (savedServices) {
      setServices(savedServices);
    } else {
      // Initialize with default services (charges 0)
      const defaultServices = DEFAULT_SERVICES.map((name) => ({
        name,
        Charges: 0,
      }));
      setServices(defaultServices);
      saveDentalServices(patientDetails.id, defaultServices);
    }
  }, [patientDetails]);

  // Calculate total
  const total = services.reduce((sum, svc) => {
    const charge = parseFloat(svc.Charges) || 0;
    return sum + charge;
  }, 0);

  // Handle charge change
  const handleChargeChange = (index, newCharge) => {
    const updated = [...services];
    updated[index].Charges = newCharge === "" ? "" : parseFloat(newCharge) || 0;
    setServices(updated);
    // Save to localStorage
    if (patientDetails?.id) {
      saveDentalServices(patientDetails.id, updated);
    }
  };

  // Process payment (UPI or Cash)
  const processPayment = (mode) => {
    if (!patientDetails?.id) {
      toast.error("Invalid appointment data.");
      return;
    }

    setLoading(true);

    // Get appointments
    const appointments = getAppointments();
    const index = appointments.findIndex((apt) => apt.id === patientDetails.id);

    if (index === -1) {
      toast.error("Appointment not found.");
      setLoading(false);
      return;
    }

    // Update appointment: set PAID and VISITED, store payment details
    const updatedAppointment = {
      ...appointments[index],
      paymentStatus: "PAID",
      appointmentvisitStatus: "VISITED",
      paymentMode: mode,
      paymentAmount: total,
      paymentDate: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    appointments[index] = updatedAppointment;
    saveAppointments(appointments);

    toast.success("Payment successful!");
    setLoading(false);

    // Show invoice
    setShowInvoice(true);
  };

  const handleUPIPayment = () => processPayment("upi");
  const handleCASHPayment = () => processPayment("cash");

  const handleCloseInvoice = () => {
    setShowInvoice(false);
    reset(); // Close this modal as well
  };

  return (
    <>
      <div
        id="login-popup"
        tabIndex="-1"
        className="bg-black/50 overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 h-full items-center justify-center flex"
      >
        <div className="relative p-4 w-full max-w-xl h-full md:h-auto">
          <div className="relative bg-white rounded-lg shadow">
            <button
              type="button"
              className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center popup-close"
              onClick={reset}
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5"
                fill="#c6c7c7"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close popup</span>
            </button>
            <div className="p-5">
              <div className="text-center">
                <p className="mb-3 text-2xl font-semibold leading-5 text-slate-900">
                  Select Payment Option
                </p>
              </div>
              <div className="grid grid-cols-1 gap-1 p-2 rounded-lg my-2">
                <div className="flex border-gray-200 py-2">
                  <span className="text-gray-900 font-semibold mx-2">
                    Services
                  </span>
                  <span className="ml-auto text-gray-900 font-semibold mx-2">
                    Amount
                  </span>
                </div>
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="flex border border-gray-500 rounded-lg py-2"
                  >
                    <span className="text-gray-500 ml-2">{service.name}</span>
                    <span className="ml-auto text-gray-900 border-l px-4 border-gray-400 mx-2">
                      <input
                        type="number"
                        value={service.Charges}
                        onChange={(e) =>
                          handleChargeChange(index, e.target.value)
                        }
                        className="w-16 border rounded"
                      />
                    </span>
                  </div>
                ))}
                <div className="flex py-2">
                  <span className="text-blue-500 font-semibold ml-2">
                    Total Amount
                  </span>
                  <span className="ml-auto text-blue-500 font-semibold text-xl px-4 border-gray-400 mx-2">
                    ₹ {total}/-
                  </span>
                </div>
              </div>
              <div className="mx-auto">
                <div className="flex flex-wrap -m-2">
                  <div className="p-2 w-1/2">
                    <button
                      type="button"
                      onClick={handleUPIPayment}
                      disabled={loading}
                      className="w-full bg-orange-400 rounded-3xl border border-gray-300 focus:ring-2 text-base outline-none text-white py-1 px-16 leading-8 transition-colors duration-200 ease-in-out disabled:opacity-50"
                    >
                      Pay using UPI
                    </button>
                  </div>
                  <div className="p-2 w-1/2">
                    <button
                      type="button"
                      onClick={handleCASHPayment}
                      disabled={loading}
                      className="w-full bg-orange-400 rounded-3xl border border-gray-300 focus:ring-2 text-base outline-none text-white py-1 px-16 leading-8 transition-colors duration-200 ease-in-out disabled:opacity-50"
                    >
                      Pay using Cash
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {loading && <Spinner />}
      {showInvoice && (
        <DentalPrintInvoice
          reset={handleCloseInvoice}
          patientDetails={patientDetails}
          services={services}
          total={total}
        />
      )}
      <ToastContainer />
    </>
  );
};

export default UnpaidtoPaid;